/* Pup Club first-party subscription client.
 *
 * The only enrollment route in the storefront. No processor, no card field, no gateway.
 * Every price, trial length and interval it shows comes from the server-published offer
 * (PK_VIP_PRICE_MAP.plan, generated from public.pup_club_offer_versions) — nothing about
 * the offer is written twice.
 *
 * TRANSPORT BOUNDARY
 * ------------------
 * resolveTransport() picks exactly one of two real routes and nothing else:
 *
 *   'supabase'        window.PK_PUP_CLUB.restUrl + .anonKey are set. Calls the first-party
 *                     RPCs: pup_club_offer, pup_club_record_consent, pup_club_enroll,
 *                     pup_club_membership_state, and the hosted V2 lifecycle contract
 *                     pup_club_request_pause_v2 / _resume_v2 / _cancel_v3 plus
 *                     pup_club_prepare_rejoin. anonKey is a publishable key, safe in a
 *                     browser; no service key ever appears here.
 *
 *                     The four legacy immediate-mutation RPCs -- the unsuffixed pause,
 *                     resume and cancel requests, and the retention-offer acceptance --
 *                     are revoked from PUBLIC, anon and authenticated on the hosted
 *                     project, and there are zero active retention offers. No executable
 *                     browser path calls or advertises any of them.
 *
 *                     What is verified is exactly that, and nothing broader: the three
 *                     V2 identifiers contain the unsuffixed names as a leading
 *                     substring, so a substring grep for the unsuffixed pause name
 *                     will always match its _v2 form. A substring count is therefore
 *                     not the acceptance test. The test is the set of executable rpc()
 *                     call sites: every lifecycle one names a _v2/_v3 function or
 *                     pup_club_prepare_rejoin, and there is no call site, handler or
 *                     rendered control for the retention-offer acceptance at all.
 *
 *                     Every V2 lifecycle call carries p_idempotency_key, a UUID this
 *                     file mints once per user action. A retry of the same in-flight
 *                     action reuses that UUID, so a repeated tap or a network retry can
 *                     never become a second server-side transition. The authenticated
 *                     identity alone selects the membership; no browser-owned membership
 *                     identifier is ever sent.
 *
 *   'shopify-contact' No Supabase endpoint configured. The consent snapshot is submitted
 *                     through the store's own contact route, which exists on every Shopify
 *                     storefront. A real durable record, no app, no spend, no card.
 *
 * Neither branch ever reports a charge, a payment method or an active membership. Only a
 * server response may say what a membership's state is.
 */
(() => {
  'use strict';

  const CFG = (typeof window !== 'undefined' && window.PK_PUP_CLUB) || {};
  const MAP = (typeof window !== 'undefined' && window.PK_VIP_PRICE_MAP) || {};
  const PLAN = MAP.plan || {};
  let authoritativeOffer = null;
  let authoritativeOfferPromise = null;
  /* Monotonic request counter. Only the newest published-offer response may
     replace the cache, so a slow earlier reply cannot overwrite a newer one. */
  let offerRequestSeq = 0;

  /* The only terms version this release may enroll against. A server offer that
     publishes any other version is treated as not ready: the shopper is told
     enrollment is not open rather than being enrolled on unreviewed terms. */
  const REQUIRED_TERMS_VERSION = '2026-10-01.1';
  /* The rest of the reviewed contract. These are the exact published values this
     release was approved against, so a server offer that moves the currency, the
     trial length, the renewal amount or the billing interval is treated as not
     ready rather than silently enrolled on numbers nobody reviewed. */
  const REQUIRED_CURRENCY = 'USD';
  const REQUIRED_TRIAL_DAYS = 21;
  const REQUIRED_RENEWAL_CENTS = 2999;
  const REQUIRED_INTERVAL_DAYS = 28;

  const money = (cents) => '$' + (Number(cents || 0) / 100).toFixed(2);
  const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? '' : 's'}`;

  /**
   * Projects one published-offer record into the shape every surface reads.
   *
   * The record is a parameter, not the module-level cache, which is the whole
   * point: a caller holding the exact response to its own forced request can
   * evaluate that response instead of whatever the shared cache happens to hold
   * at the moment it looks. Only the newest request may replace the cache, so an
   * earlier forced read and a later one are genuinely different records and must
   * never be judged against each other.
   *
   * `fallback` is the theme's build-time catalog record, and it is supplied only
   * by the presentation path. It may fill in numbers before the published offer
   * arrives; it may never satisfy a contract clause, so the exact-snapshot
   * projection is given no fallback at all.
   */
  function projectOffer(record, fallback) {
    const fetched = !!(record && typeof record === 'object');
    const live = fetched ? record : {};
    const fb = fallback && typeof fallback === 'object' ? fallback : {};
    const enrollment = live.enrollment && typeof live.enrollment === 'object'
      ? live.enrollment
      : null;
    return {
      consentVersion: typeof live.consentVersion === 'string' ? live.consentVersion : (fb.consentVersion || null),
      offerHash: typeof live.offerHash === 'string' ? live.offerHash : (fb.offerHash || null),
      trialDays: Number.isInteger(live.trialDays)
        ? live.trialDays
        : (Number.isInteger(fb.trialDays) ? fb.trialDays : null),
      renewalCents: Number.isInteger(live.renewalCents)
        ? live.renewalCents
        : (Number.isInteger(fb.renewalCents) ? fb.renewalCents : null),
      intervalDays: Number.isInteger(live.intervalDays)
        ? live.intervalDays
        : (Number.isInteger(fb.intervalDays) ? fb.intervalDays : null),
      currency: typeof live.currency === 'string' ? live.currency : (fb.currency || 'USD'),
      /* Enrollment and provider capability are server facts only. Before the
         published offer has been fetched there is no theme fallback to fall back
         to: the answer is false. A browser constant may never open enrollment.
         The three accepted spellings are the ones the published contract uses for
         the same flag; each must be exactly true, so an absent or truthy-but-not-
         true value still reads as closed. */
      enrollmentEnabled: !!(fetched && enrollment && (
        enrollment.open === true
        || enrollment.enrollmentEnabled === true
        || enrollment.enrollment_enabled === true
      )),
      billing: fetched && live.billing && typeof live.billing === 'object'
        ? live.billing
        : { providerKey: null, configured: false,
            recurringCaptureConfigured: false, paymentMethodCaptureConfigured: false },
      // The offer id is the server's own UUID for the current published offer.
      // pup_club_prepare_rejoin must be handed this value, never a browser constant.
      offerId: uuidLike(live.offerId) ? live.offerId
        : (uuidLike(live.offer_id) ? live.offer_id : null),
    };
  }

  /** The cached offer, exactly as the newest completed response published it.
   *  This is the presentation answer. A mutation may not decide from it. */
  function offer() {
    return projectOffer(authoritativeOffer, {
      consentVersion: PLAN.consentVersion,
      offerHash: PLAN.offerHash,
      trialDays: PLAN.trialDays,
      renewalCents: PLAN.recurringCents,
      intervalDays: PLAN.intervalDays,
      currency: MAP.currency,
    });
  }

  /* The exact response to one forced request, projected on its own terms. No
     theme fallback and no cache: an action decides on what the server just told
     it, or on nothing. */
  const exactOffer = (record) => projectOffer(record, null);

  /**
   * The complete enrollment contract, evaluated against one offer record.
   *
   * Every clause must hold on the server's own published offer: enrollment open,
   * billing configured, recurring capture configured, payment-method capture
   * configured, the reviewed terms version, the reviewed currency, trial length,
   * renewal amount and billing interval, and a real server offer UUID. Any
   * missing or false clause is a refusal, never a warning. Callers that add a
   * cart line or start a trial must gate on this and on nothing else — there is
   * deliberately no second, looser gate anywhere in the theme.
   *
   * `candidate` is the record being judged. When one is supplied it is the whole
   * subject of the verdict: the caller is holding the exact response to its own
   * forced request, so what the shared cache holds — including whether it holds
   * anything at all — says nothing about the record in hand. Without a candidate
   * the cache is the record, and an empty cache is still a refusal.
   */
  function offerReadiness(candidate) {
    const explicit = candidate && typeof candidate === 'object'
      /* A projection already carries the resolved flags; a raw published record
         still has to be projected before any clause can be read off it. */
      ? (typeof candidate.enrollmentEnabled === 'boolean' ? candidate : exactOffer(candidate))
      : null;
    const o = explicit || offer();
    const billing = o && o.billing && typeof o.billing === 'object' ? o.billing : null;
    const reasons = [];
    if (!explicit && !authoritativeOffer) reasons.push('offer_not_fetched');
    if (!o || o.enrollmentEnabled !== true) reasons.push('enrollment_closed');
    if (!billing || billing.configured !== true) reasons.push('billing_not_configured');
    if (!billing || billing.recurringCaptureConfigured !== true) reasons.push('recurring_capture_not_configured');
    if (!billing || billing.paymentMethodCaptureConfigured !== true) reasons.push('payment_method_capture_not_configured');
    if (!o || o.consentVersion !== REQUIRED_TERMS_VERSION) reasons.push('terms_version_mismatch');
    if (!o || String(o.currency || '').toUpperCase() !== REQUIRED_CURRENCY) reasons.push('currency_mismatch');
    if (!o || o.trialDays !== REQUIRED_TRIAL_DAYS) reasons.push('trial_days_mismatch');
    if (!o || o.renewalCents !== REQUIRED_RENEWAL_CENTS) reasons.push('renewal_cents_mismatch');
    if (!o || o.intervalDays !== REQUIRED_INTERVAL_DAYS) reasons.push('interval_days_mismatch');
    if (!o || !uuidLike(o.offerId)) reasons.push('offer_id_unavailable');
    return { ready: reasons.length === 0, reasons };
  }

  /** True only when the freshly fetched server offer satisfies every clause. */
  const enrollmentReady = () => offerReadiness().ready;

  /* One sentence for every surface that has to refuse. It states what is true and
     what did not happen, and promises nothing about when that changes. */
  const ENROLLMENT_CLOSED_MESSAGE =
    'Pup Club enrollment is not open yet. Nothing has been added, started or charged.';

  /**
   * The approved offer sentence, built from the published numbers.
   * One function, so join, terms, help, portal and cancel can never drift.
   *
   * `snapshot` is optional and exists for the mutation paths: a shopper is told,
   * and consents to, the terms of the exact response that authorized the action,
   * not the terms of whatever landed in the cache while they were reading.
   * Presentation callers pass nothing and get the cached offer.
   */
  function offerSentence(snapshot) {
    const o = snapshot && typeof snapshot === 'object' ? snapshot : offer();
    if (!Number.isInteger(o.trialDays) || !Number.isInteger(o.renewalCents)
        || !Number.isInteger(o.intervalDays)) return '';
    // "21-day trial" is adjectival, so it stays singular and hyphenated. The interval
    // is a plain count and pluralises. This must match pkOfferSentence() in the portal.
    return `${o.trialDays}-day trial, then ${money(o.renewalCents)} every `
         + `${plural(o.intervalDays, 'day')} until you cancel.`;
  }

  function renewalPhrase(snapshot) {
    const o = snapshot && typeof snapshot === 'object' ? snapshot : offer();
    if (!Number.isInteger(o.renewalCents) || !Number.isInteger(o.intervalDays)) return '';
    return `${money(o.renewalCents)} every ${plural(o.intervalDays, 'day')}`;
  }

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const uuidLike = (v) => typeof v === 'string' && UUID_RE.test(v);

  /* One opaque UUID per user action. crypto.randomUUID() is the real source; the
     getRandomValues fallback exists for the older WebViews that ship a Crypto object
     without randomUUID. There is deliberately no Math.random path: a weak key could
     collide with another member's in-flight request, and the server treats the key as
     the identity of the transition. Without a cryptographic source we refuse rather
     than send a guessable key. */
  function newIdempotencyKey() {
    const c = (typeof crypto !== 'undefined' && crypto) || null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (c && typeof c.getRandomValues === 'function') {
      const b = c.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const hex = Array.from(b, (n) => n.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-`
           + `${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    const e = new Error('no_secure_random');
    e.userMessage = 'This browser cannot safely make that change. Please update it, or contact us and we will do it for you.';
    throw e;
  }

  function resolveTransport() {
    if (typeof CFG.restUrl === 'string' && /^https:\/\/[a-z0-9.-]+\/rest\/v1\/?$/i.test(CFG.restUrl)
        && typeof CFG.anonKey === 'string' && CFG.anonKey.length > 20) {
      return 'supabase';
    }
    return 'shopify-contact';
  }

  const emailLooksValid = (v) =>
    typeof v === 'string' && /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(v.trim()) && v.trim().length <= 254;

  // ---------------------------------------------------------------- supabase transport
  async function rpc(name, body, { token } = {}) {
    const headers = {
      'Content-Type': 'application/json',
      apikey: CFG.anonKey,
      Authorization: 'Bearer ' + (token || CFG.anonKey),
      Accept: 'application/json',
    };
    const res = await fetch(CFG.restUrl.replace(/\/?$/, '/') + 'rpc/' + name, {
      method: 'POST', headers, body: JSON.stringify(body || {}), credentials: 'omit',
    });
    const text = await res.text();
    let parsed = null;
    try { parsed = text ? JSON.parse(text) : null; } catch { /* non-JSON error body */ }
    if (!res.ok) {
      const code = (parsed && (parsed.message || parsed.hint)) || `http_${res.status}`;
      const err = new Error(String(code).slice(0, 200));
      err.status = res.status;
      throw err;
    }
    return parsed;
  }

  function normalizeOffer(payload) {
    if (!payload || typeof payload !== 'object'
        || !Number.isInteger(payload.trialDays)
        || !Number.isInteger(payload.renewalCents)
        || !Number.isInteger(payload.intervalDays)
        || typeof payload.offerHash !== 'string'
        || typeof payload.consentVersion !== 'string') return null;
    return {
      consentVersion: payload.consentVersion,
      offerHash: payload.offerHash,
      trialDays: payload.trialDays,
      renewalCents: payload.renewalCents,
      intervalDays: payload.intervalDays,
      currency: typeof payload.currency === 'string' ? payload.currency : 'USD',
      enrollment: payload.enrollment && typeof payload.enrollment === 'object' ? payload.enrollment : {},
      billing: payload.billing && typeof payload.billing === 'object' ? payload.billing : {},
      // Carried so rejoin can quote the server's current offer UUID back to it.
      // No retention offer is read or published: the retention RPC is revoked and
      // the hosted contract has zero active offers, so there is nothing to advertise.
      offerId: uuidLike(payload.offerId) ? payload.offerId
        : (uuidLike(payload.offer_id) ? payload.offer_id : null),
    };
  }

  /* One published-offer request. The cache is replaced only when the response
     parses as a valid published offer AND this is still the newest request in
     flight; an invalid payload or a network failure rejects, leaving whatever
     was cached before untouched so no caller can read a half-applied offer. */
  function fetchPublishedOffer() {
    const seq = ++offerRequestSeq;
    return rpc('pup_club_offer', {}).then((payload) => {
      const normalized = normalizeOffer(payload);
      if (!normalized) throw new Error('invalid_published_offer');
      if (seq === offerRequestSeq) {
        authoritativeOffer = normalized;
        try { window.dispatchEvent(new Event('pk:offer-state')); } catch { /* optional renderer refresh */ }
      }
      return normalized;
    });
  }

  /* The page may keep product imagery and public catalog data in its theme map,
     but trial terms, price, offer hash and processor capability always come
     from the public Supabase offer. This prevents a built-time assumption from
     becoming a customer-facing consent contract.
     ---------------------------------------------------------------------------
     Without { force: true } this is the first-paint path: a cached offer, or an
     in-flight request for one, is reused, because presentation only needs the
     published numbers once.
     With { force: true } it is the pre-mutation path, and it is the reason this
     function exists in two modes. Both the cached offer and any in-flight
     request are bypassed and a new pup_club_offer call is sent, so an answer
     computed earlier in the page's life — or a request that started before the
     shopper acted — can never stand in for the server's current position. The
     cache is replaced only on a valid response, the offer-state event fires so
     every renderer re-decides, and a network or validation failure throws: the
     caller fails closed instead of falling back to the value it already had. */
  async function refreshOffer({ force = false } = {}) {
    if (resolveTransport() !== 'supabase') {
      if (!force) return null;
      const e = new Error('offer_transport_unavailable');
      e.userMessage = ENROLLMENT_CLOSED_MESSAGE;
      throw e;
    }
    if (!force) {
      if (authoritativeOffer) return authoritativeOffer;
      if (authoritativeOfferPromise) return authoritativeOfferPromise;
      const pending = fetchPublishedOffer()
        .finally(() => { if (authoritativeOfferPromise === pending) authoritativeOfferPromise = null; });
      authoritativeOfferPromise = pending;
      return pending;
    }
    return fetchPublishedOffer();
  }

  /**
   * The only readiness answer a mutation may act on.
   *
   * It forces a network refresh first and then evaluates the one canonical
   * contract above against what the server has just published. A transport,
   * network or validation failure is reported as not ready with a reason — it is
   * never swallowed, and it never leaves a caller reading a cached verdict.
   * `fresh` states which of those two happened, so a caller can say so honestly.
   *
   * The verdict is computed against the exact response to this call's own
   * request, and that response is handed back as `offer`. Reading the shared
   * cache here would be a race: only the newest request may replace the cache,
   * so an earlier forced read whose server said "closed" would otherwise be
   * judged against a newer, ready record — or against an older one a newer
   * request is about to displace. The caller acts on `offer` and on nothing
   * else, for the whole action; it is null whenever the verdict is not ready.
   */
  async function freshOfferReadiness() {
    let response = null;
    try {
      response = await refreshOffer({ force: true });
    } catch (_) {
      return { ready: false, reasons: ['offer_unavailable'], fresh: false, offer: null };
    }
    if (!response || typeof response !== 'object') {
      return { ready: false, reasons: ['offer_unavailable'], fresh: false, offer: null };
    }
    const exact = exactOffer(response);
    const readiness = offerReadiness(exact);
    return {
      ready: readiness.ready,
      reasons: readiness.reasons,
      fresh: true,
      offer: readiness.ready ? exact : null,
    };
  }

  /* A browser value is not an authenticated session. The original client saved an
     unverified bare access token, which let any localStorage string render Account
     after a reload. Keep a refreshable session envelope and validate it with
     Supabase before we expose any member UI. */
  const SESSION_KEY = 'pk-pup-club-session-v2';
  const LEGACY_SESSION_KEY = 'pk-pup-club-access-token';
  const SIGNOUT_KEY = 'pk-pup-club-signout-gen';
  const REFRESH_LOCK = 'pk-pup-club-refresh';
  const REFRESH_LEASE = 'pk-pup-club-refresh-lease';
  const AUTH_EVENT = 'pk:auth-state';
  const AUTH_CHANNEL_NAME = 'pk-pup-club-auth-v1';
  const SESSION_VALID = 'valid';
  const SESSION_INVALID = 'invalid';
  const SESSION_INDETERMINATE = 'indeterminate';
  const AUTH_TIMEOUT_MS = 8000;
  const RETRY_DELAYS = [1500, 4000, 10000, 30000, 60000];
  let authChannel = null, authenticatedEmail = null;
  let sessionVerified = false, reviewToken = null, verifiedToken = null, verifiedAt = 0;
  let sessionRevision = 0, authPhase = 'guest', reconnecting = false, retryAttempt = 0;
  let validationFlight = null, accountFlight = null, membershipFlight = null, recoveryFlight = null;
  let retryTimer = null, refreshTimer = null, activityTimer = null;
  let locallySignedOut = false, memoryOnly = false, newAuthPending = null, signedOutSid = null;
  const authRequests = new Set();
  const randomMarker = () => typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  const tabId = randomMarker();
  const nowSeconds = () => Math.floor(Date.now() / 1000);
  const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
  const later = (fn, ms) => { const id = setTimeout(fn, ms); if (id && typeof id.unref === 'function') id.unref(); return id; };
  const pauseFor = ms => new Promise(resolve => later(resolve, ms));
  const session = {
    value: null,
    set(record) { this.value = record || null; },
    get() { return this.value && this.value.accessToken || null; },
    record() { return this.value; },
  };
  function verifiedSession() {
    const value = session.record();
    return sessionVerified && !!value && verifiedToken === value.accessToken
      && (!value.expiresAt || value.expiresAt > nowSeconds());
  }
  const isReviewAccount = () => verifiedSession() && reviewToken === session.get();
  function staleSession() { const error = new Error('stale_session'); error.stale = true; return error; }
  function assertSessionRevision(revision) { if (revision !== sessionRevision) throw staleSession(); }
  function sessionIsCurrent(token, revision) {
    return revision === sessionRevision && verifiedSession() && !!token && session.get() === token;
  }
  function assertVerifiedSession(token, revision) { if (!sessionIsCurrent(token, revision)) throw staleSession(); }
  // Supabase also issues 12-character legacy refresh tokens. Retaining this
  // opaque value grants no authority: every replacement still needs live /user proof.
  const opaqueToken = token => typeof token === 'string' && /^[A-Za-z0-9._~-]{12,2048}$/.test(token);
  const authBaseUrl = () => CFG.restUrl.replace(/\/rest\/v1\/?$/, '/auth/v1');
  function storageBackends() {
    const stores = [];
    try { if (window.localStorage) stores.push(window.localStorage); } catch (_) {}
    try { if (window.sessionStorage) stores.push(window.sessionStorage); } catch (_) {}
    return stores;
  }
  function signoutGeneration() {
    for (const store of storageBackends()) {
      try { return store.getItem(SIGNOUT_KEY) || null; } catch (_) {}
    }
    return null;
  }
  function tokenExpiry(token) {
    // A timing hint only. No JWT claim, sid or storage field can authenticate a user.
    try {
      const value = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      return Number.isFinite(value.exp) && value.exp > 1000000000 ? value.exp : null;
    } catch (_) { return null; }
  }
  function normalizeSession(record) {
    if (!record || typeof record !== 'object' || !jwtLike(record.accessToken)) return null;
    const expiry = tokenExpiry(record.accessToken) || Number(record.expiresAt);
    return {
      accessToken: record.accessToken,
      refreshToken: opaqueToken(record.refreshToken) ? record.refreshToken : null,
      expiresAt: Number.isFinite(expiry) && expiry > 1000000000 ? expiry : null,
      sid: typeof record.sid === 'string' && /^[A-Za-z0-9_-]{8,100}$/.test(record.sid) ? record.sid : null,
      generation: typeof record.generation === 'string' ? record.generation : null,
    };
  }
  function recordFromAuthBody(body) {
    const source = body && body.session && typeof body.session === 'object' ? body.session : body;
    if (!source || typeof source !== 'object') return null;
    const expiresAt = Number(source.expires_at), expiresIn = Number(source.expires_in);
    return normalizeSession({ accessToken: source.access_token, refreshToken: source.refresh_token,
      expiresAt: Number.isFinite(expiresAt) && expiresAt > 1000000000 ? expiresAt
        : Number.isFinite(expiresIn) && expiresIn > 0 ? nowSeconds() + expiresIn : null });
  }
  const sameEnvelope = (a, b) => !!a && !!b && a.accessToken === b.accessToken
    && a.refreshToken === b.refreshToken && a.sid === b.sid && a.generation === b.generation;
  function readStoredEnvelope({ includeMemory = true } = {}) {
    const generation = signoutGeneration();
    for (const store of storageBackends()) {
      try {
        const raw = store.getItem(SESSION_KEY);
        const record = raw && normalizeSession(JSON.parse(raw));
        if (record && record.generation === generation) return record;
        // A readable local store is authoritative even when empty. An old copy
        // in this tab's sessionStorage must not resurrect another tab's logout.
        if (!memoryOnly) return null;
        break;
      } catch (_) { /* Inaccessible storage never grants identity. */ }
    }
    const record = session.record();
    return includeMemory && record && record.generation === generation ? record : null;
  }
  function storedSession() { return locallySignedOut ? null : readStoredEnvelope(); }
  function sessionHint() { return storedSession() ? 'stored' : 'none'; }
  function authStatus() {
    const verified = verifiedSession();
    const phase = verified ? 'verified' : authPhase === 'verified' ? 'reconnecting' : authPhase;
    return { phase, reviewAccount: isReviewAccount(),
      pending: !verified && ['verifying', 'reconnecting'].includes(phase),
      reconnecting: reconnecting || phase === 'reconnecting', retryInMs: RETRY_DELAYS[Math.min(Math.max(retryAttempt - 1, 0), RETRY_DELAYS.length - 1)] };
  }
  function announceAuthState(reason) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    const state = authStatus();
    try { window.dispatchEvent(new CustomEvent(AUTH_EVENT, { detail: {
      authenticated: verifiedSession(), reviewAccount: state.reviewAccount,
      pending: state.pending, reconnecting: state.reconnecting, reason: String(reason || 'changed'),
    } })); } catch (_) {}
  }
  function cancelAuthWork() {
    for (const timer of [retryTimer, refreshTimer, activityTimer]) if (timer) clearTimeout(timer);
    retryTimer = refreshTimer = activityTimer = null;
    for (const controller of authRequests) controller.abort();
    authRequests.clear();
    validationFlight = accountFlight = membershipFlight = recoveryFlight = null;
  }
  function dropAuthority() {
    sessionRevision += 1; sessionVerified = false; verifiedToken = reviewToken = null;
    authenticatedEmail = null; verifiedAt = 0;
  }
  function clearStoredSession({ broadcast = true, reason = 'signed-out', expected = null, remote = false } = {}) {
    const current = readStoredEnvelope() || session.record();
    // A losing refresh may not erase the winner or a newly signed-in account.
    if (expected && !sameEnvelope(current, expected)) return false;
    const sid = current && current.sid || null;
    signedOutSid = sid;
    dropAuthority(); cancelAuthWork(); session.set(null); locallySignedOut = true; memoryOnly = false;
    authPhase = 'guest'; reconnecting = false; retryAttempt = 0;
    const generation = randomMarker();
    storageBackends().forEach(store => {
      // Quota may block writes while removal still works. Attempt each independently.
      if (!remote) { try { store.setItem(SIGNOUT_KEY, generation); } catch (_) {} }
      try { store.removeItem(SESSION_KEY); } catch (_) {}
      try { store.removeItem(LEGACY_SESSION_KEY); } catch (_) {}
    });
    if (broadcast) { try { authChannel && authChannel.postMessage({ type: 'signout', sid }); } catch (_) {} }
    announceAuthState(reason);
    return true;
  }
  function writeEnvelope(record) {
    let firstWritten = false;
    storageBackends().forEach((store, index) => {
      try { store.setItem(SESSION_KEY, JSON.stringify(record)); store.removeItem(LEGACY_SESSION_KEY); if (index === 0) firstWritten = true; } catch (_) {}
    });
    memoryOnly = !firstWritten;
  }
  function storeSession(record, { reason = 'session-set', announce = true, verified = false,
    expected = null, generation = signoutGeneration() } = {}) {
    const normalized = normalizeSession(record);
    if (!normalized) { clearStoredSession({ reason: 'invalid-session' }); return null; }
    if (expected && (locallySignedOut || generation !== signoutGeneration()
      || !sameEnvelope(readStoredEnvelope(), expected))) throw staleSession();
    normalized.sid = normalized.sid || expected && expected.sid || randomMarker();
    normalized.generation = generation;
    const changed = !sameEnvelope(session.record(), normalized);
    if (changed) sessionRevision += 1;
    session.set(normalized); locallySignedOut = false;
    // verified=true is usable only with this exact token's live /user proof.
    sessionVerified = verified === true && verifiedToken === normalized.accessToken;
    if (!sessionVerified) { verifiedToken = reviewToken = null; authenticatedEmail = null; }
    writeEnvelope(normalized);
    if (sessionVerified) { authPhase = 'verified'; reconnecting = false; retryAttempt = 0; scheduleRefresh(); }
    if (announce) announceAuthState(reason);
    return normalized;
  }
  function storeSessionToken(token) {
    return token && jwtLike(token) ? storeSession({ accessToken: token }) : storeSession(null);
  }
  async function authRequest(path, options = {}) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    if (controller) authRequests.add(controller);
    let timer;
    const canceled = new Promise((_, reject) => {
      const fail = () => reject(new Error('auth_request_interrupted'));
      if (controller) controller.signal.addEventListener('abort', fail, { once: true });
      timer = later(() => { if (controller) controller.abort(); else fail(); }, AUTH_TIMEOUT_MS);
    });
    try {
      return await Promise.race([canceled, (async () => {
        const res = await fetch(authBaseUrl() + path, { ...options, credentials: 'omit',
          ...(controller ? { signal: controller.signal } : {}) });
        let body = null;
        try { body = await res.json(); } catch (_) {}
        return { ok: res.ok, status: res.status, body };
      })()]);
    } finally { if (timer) clearTimeout(timer); if (controller) authRequests.delete(controller); }
  }
  const authErrorCode = body => String(body && (body.error_code || body.code || body.error) || '').toLowerCase();
  function invalidRefreshResponse(status, body) {
    if (![400, 401, 403].includes(status)) return false;
    return ['refresh_token_not_found', 'refresh_token_already_used', 'session_not_found', 'session_expired',
      'invalid_grant', 'user_not_found', 'user_banned'].includes(authErrorCode(body));
  }
  async function inspectUserSession(accessToken, assertCurrent) {
    if (resolveTransport() !== 'supabase' || !jwtLike(accessToken)) return { status: SESSION_INVALID };
    try {
      const res = await authRequest('/user', { headers: { apikey: CFG.anonKey, Authorization: 'Bearer ' + accessToken } });
      assertCurrent();
      if (!res.ok) return { status: res.status === 401 || (res.status === 403 &&
        ['bad_jwt', 'jwt_expired', 'session_not_found', 'user_not_found', 'user_banned'].includes(authErrorCode(res.body)))
        ? SESSION_INVALID : SESSION_INDETERMINATE };
      const user = res.body;
      if (!user || typeof user !== 'object' || typeof user.id !== 'string' || !user.id.trim()) return { status: SESSION_INDETERMINATE };
      return { status: SESSION_VALID, user };
    } catch (error) { if (error && error.stale) throw error; assertCurrent(); return { status: SESSION_INDETERMINATE }; }
  }
  function acceptVerified(record, user, { expected = null, generation = signoutGeneration(), persist = false } = {}) {
    if (expected && (generation !== signoutGeneration() || !sameEnvelope(readStoredEnvelope(), expected))) throw staleSession();
    // The sole proof-producing path: a positive /user for this exact access token.
    verifiedToken = record.accessToken; sessionVerified = true;
    reviewToken = user.app_metadata && user.app_metadata.merchant_review === true ? record.accessToken : null;
    authenticatedEmail = emailLooksValid(user.email) ? String(user.email).trim().toLowerCase() : null;
    verifiedAt = Date.now();
    if (persist) return storeSession(record, { verified: true, expected, generation, announce: false });
    if (session.get() && session.get() !== record.accessToken) sessionRevision += 1;
    session.set(record); locallySignedOut = false; authPhase = 'verified'; reconnecting = false; retryAttempt = 0;
    if (retryTimer) clearTimeout(retryTimer); retryTimer = null;
    scheduleRefresh();
    return record;
  }
  function validation(status, record = null) { return { status, record, revision: sessionRevision }; }
  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer); refreshTimer = null;
    const record = session.record();
    if (!record || !record.refreshToken || !record.expiresAt || !visible() || !verifiedSession()) return;
    const remaining = record.expiresAt * 1000 - Date.now();
    const delay = Math.max(1000, remaining - Math.min(120000, remaining / 2));
    refreshTimer = later(() => { refreshTimer = null; if (visible()) void recoverSession({ refresh: true }); }, delay);
  }
  function scheduleRetry() {
    if (retryTimer || locallySignedOut || !storedSession()) return;
    const delay = RETRY_DELAYS[Math.min(retryAttempt++, RETRY_DELAYS.length - 1)];
    retryTimer = later(() => { retryTimer = null; void recoverSession(); }, delay);
  }
  function markReconnecting() {
    reconnecting = true; authPhase = 'reconnecting';
    if (!verifiedSession()) { sessionVerified = false; verifiedToken = reviewToken = null; authenticatedEmail = null; }
    announceAuthState('reconnecting'); scheduleRetry();
  }
  async function withRefreshLock(work, assertCurrent) {
    const locks = typeof navigator !== 'undefined' && navigator.locks;
    if (locks && typeof locks.request === 'function') {
      return locks.request(REFRESH_LOCK, { mode: 'exclusive' }, async () => { assertCurrent(); return work(() => {}); });
    }
    // Older browsers: a token-free, bounded lease. Re-check ownership before every
    // refresh write. If coordination storage is unavailable, retain pending state.
    let store;
    try { store = window.localStorage; if (!store) throw Error(); store.getItem(REFRESH_LEASE); } catch (_) { throw new Error('auth_coordination_unavailable'); }
    const owner = tabId + '-' + randomMarker(), deadline = Date.now() + 35000;
    const read = () => { try { return JSON.parse(store.getItem(REFRESH_LEASE) || 'null'); } catch (_) { return null; } };
    while (Date.now() < deadline) {
      assertCurrent();
      const held = read();
      if (!held || held.until < Date.now()) {
        store.setItem(REFRESH_LEASE, JSON.stringify({ owner, until: Date.now() + 30000 }));
        await pauseFor(35); assertCurrent();
        if (read()?.owner === owner) {
          const check = () => { assertCurrent(); if (read()?.owner !== owner) throw staleSession(); };
          try { return await work(check); } finally { if (read()?.owner === owner) store.removeItem(REFRESH_LEASE); }
        }
      }
      await pauseFor(75);
    }
    throw new Error('auth_coordination_busy');
  }
  async function validateSessionAttempt(record, { preserveOnIndeterminate = false, refresh = false, force = false, newSession = false, requireReview = false } = {}) {
    const normalized = normalizeSession(record);
    if (!normalized) return validation(SESSION_INVALID);
    const revision = sessionRevision, generation = signoutGeneration();
    const baseline = readStoredEnvelope({ includeMemory: !newSession });
    const persisted = !newSession && sameEnvelope(baseline, normalized);
    const assertCurrent = () => {
      assertSessionRevision(revision);
      if (generation !== signoutGeneration() || locallySignedOut
        || (newSession && !sameSnapshot(baseline, readStoredEnvelope({ includeMemory: false })))) throw staleSession();
    };
    // A cached envelope is never a verified identity. Only reuse proof obtained on
    // this page for the same unexpired token, briefly, to collapse UI read storms.
    if (!force && !refresh && sameEnvelope(session.record(), normalized) && verifiedSession() && Date.now() - verifiedAt < 30000) {
      return validation(SESSION_VALID, normalized);
    }
    if (!session.record()) session.set(normalized);
    if (!verifiedSession()) { authPhase = 'verifying'; announceAuthState('verifying'); }
    const preserve = candidate => {
      assertCurrent();
      if (preserveOnIndeterminate && !session.record()) session.set(candidate);
      markReconnecting(); return validation(SESSION_INDETERMINATE, candidate);
    };
    const prove = async (candidate, { expected = null, persist = false } = {}) => {
      const access = await inspectUserSession(candidate.accessToken, assertCurrent); assertCurrent();
      if (access.status === SESSION_VALID) {
        if (requireReview && access.user.app_metadata?.merchant_review !== true) return { ...validation(SESSION_INVALID, candidate), reviewRejected: true };
        const accepted = acceptVerified(candidate, access.user, { expected, generation, persist });
        return validation(SESSION_VALID, accepted);
      }
      return validation(access.status, candidate);
    };
    const due = !!normalized.refreshToken && normalized.expiresAt && normalized.expiresAt * 1000 - Date.now() < 90000;
    if (!refresh && !due) {
      const access = await prove(normalized, { expected: persisted ? normalized : newSession ? baseline : null, persist: newSession });
      if (access.status === SESSION_VALID) return access;
      if (access.status === SESSION_INDETERMINATE) return preserve(normalized);
      if (access.reviewRejected) return access;
      // A definitive /user rejection revokes this token's proof immediately,
      // even if its decoded expiry hint has not arrived yet.
      if (verifiedToken === normalized.accessToken) {
        sessionVerified = false; verifiedToken = reviewToken = null; authenticatedEmail = null;
      }
    }
    if (!normalized.refreshToken || resolveTransport() !== 'supabase') return validation(SESSION_INVALID, normalized);
    try {
      return await withRefreshLock(async checkLease => {
        assertCurrent(); checkLease();
        const latest = readStoredEnvelope();
        if (persisted && !latest) throw staleSession();
        if (!newSession && latest && !sameEnvelope(latest, normalized)) {
          const adopted = await prove(latest, { expected: latest });
          return adopted.status === SESSION_INDETERMINATE ? preserve(latest) : adopted;
        }
        const expected = persisted ? normalized : newSession ? baseline : null;
        const res = await authRequest('/token?grant_type=refresh_token', { method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: CFG.anonKey },
          body: JSON.stringify({ refresh_token: normalized.refreshToken }) });
        assertCurrent(); checkLease();
        const winner = readStoredEnvelope();
        if (persisted && winner && !sameEnvelope(winner, normalized)) {
          const adopted = await prove(winner, { expected: winner });
          return adopted.status === SESSION_INDETERMINATE ? preserve(winner) : adopted;
        }
        if (persisted && !winner) throw staleSession();
        if (!res.ok) return invalidRefreshResponse(res.status, res.body)
          ? validation(SESSION_INVALID, normalized) : preserve(normalized);
        const next = recordFromAuthBody(res.body);
        if (!next) return preserve(normalized);
        next.sid = normalized.sid || randomMarker(); next.generation = generation;
        const access = await inspectUserSession(next.accessToken, assertCurrent); assertCurrent(); checkLease();
        if (access.status === SESSION_VALID) {
          if (requireReview && access.user.app_metadata?.merchant_review !== true) return validation(SESSION_INVALID, normalized);
          const accepted = acceptVerified(next, access.user, { expected, generation, persist: true });
          return validation(SESSION_VALID, accepted);
        }
        if (access.status === SESSION_INVALID) return validation(SESSION_INVALID, normalized);
        // Rotation already happened server-side. Save the replacement without proof,
        // so a later retry never reuses the spent token. No auth/benefit is granted.
        storeSession(next, { expected, generation, announce: false });
        markReconnecting(); return validation(SESSION_INDETERMINATE, next);
      }, assertCurrent);
    } catch (error) {
      if (error && error.stale) throw error;
      return preserve(normalized);
    }
  }
  function validateStoredSession(record, options = {}) {
    const normalized = normalizeSession(record);
    if (validationFlight && sameEnvelope(validationFlight.record, normalized)) return validationFlight.promise;
    const entry = { record: normalized, promise: null };
    entry.promise = Promise.resolve().then(() => validateSessionAttempt(normalized, options)).catch(error => {
      // A first rotation of a legacy envelope has no sid to correlate. Its
      // storage event drops authority; re-prove the current envelope once.
      // Explicit logout and new sign-in attempts must never follow this path.
      const current = storedSession();
      if (!error?.stale || options.newSession || options.retried || !current || locallySignedOut) throw error;
      if (validationFlight === entry) validationFlight = null;
      return validateStoredSession(current, { ...options, retried: true });
    }).finally(() => { if (validationFlight === entry) validationFlight = null; });
    validationFlight = entry; return entry.promise;
  }
  function recoverSession({ refresh = false } = {}) {
    if (recoveryFlight) return recoveryFlight;
    if (locallySignedOut || newAuthPending || pendingEmailTokenHash || !storedSession()) return Promise.resolve(null);
    if (retryTimer) clearTimeout(retryTimer); retryTimer = null;
    const record = storedSession(), wasVerified = verifiedSession();
    const promise = Promise.resolve().then(async () => {
      try {
        const checked = await validateStoredSession(record, { preserveOnIndeterminate: true, refresh, force: true });
        if (checked.status === SESSION_VALID) announceAuthState(wasVerified ? 'session-refreshed' : 'session-restored');
        else if (checked.status === SESSION_INVALID) {
          if (!clearStoredSession({ expected: checked.record || record, reason: 'session-expired' })) scheduleRetry();
        }
        return checked;
      } catch (error) { if (!error?.stale) markReconnecting(); return null; }
    });
    recoveryFlight = promise;
    promise.finally(() => { if (recoveryFlight === promise) recoveryFlight = null; });
    return promise;
  }
  function queueRecovery() {
    if (activityTimer || !visible() || locallySignedOut || !storedSession()) return;
    activityTimer = later(() => { activityTimer = null; void recoverSession(); }, 0);
  }
  function remoteSessionChanged() {
    const incoming = readStoredEnvelope(), current = session.record();
    if (!incoming) { clearStoredSession({ broadcast: false, reason: 'remote-signout', remote: true }); return; }
    // If storage was blocked at logout, an old delayed event is not a new login.
    if (locallySignedOut && incoming.sid === signedOutSid) return;
    locallySignedOut = false;
    if (sameEnvelope(current, incoming) && verifiedSession()) return;
    if (current && current.sid && current.sid === incoming.sid && current.generation === incoming.generation) {
      // sid coordinates rotation only. Keep proof solely for the old, unexpired
      // token until /user proves the replacement; never transfer review flags.
      if (validationFlight) {
        // A waiting refresh already re-reads the shared envelope inside its lock.
        // Starting a second proof here could invalidate that useful first request.
        validationFlight.promise.finally(() => {
          if (!sameEnvelope(session.record(), readStoredEnvelope()) || !verifiedSession()) queueRecovery();
        }).catch(() => {});
      } else queueRecovery();
      return;
    }
    dropAuthority(); cancelAuthWork(); session.set(null); authPhase = 'verifying'; reconnecting = false;
    announceAuthState('account-changed'); queueRecovery();
  }
  function installCrossTabSessionSync() {
    if (typeof window === 'undefined') return;
    try {
      if (typeof BroadcastChannel === 'function') {
        authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        authChannel.addEventListener('message', event => {
          const message = event && event.data;
          if (!message || message.type !== 'signout') return;
          const current = readStoredEnvelope() || session.record();
          // A late logout for the previous account must not clear the new one.
          if (current && current.sid !== (message.sid || null)) return;
          clearStoredSession({ broadcast: false, reason: 'remote-signout', remote: true });
        });
      }
    } catch (_) { authChannel = null; }
    window.addEventListener('storage', event => {
      if (event.key === SESSION_KEY || event.key === SIGNOUT_KEY || event.key === null) remoteSessionChanged();
    });
    window.addEventListener('focus', queueRecovery);
    window.addEventListener('online', queueRecovery);
    window.addEventListener('pageshow', event => { if (event.persisted) queueRecovery(); });
    if (typeof document !== 'undefined' && document.addEventListener) document.addEventListener('visibilitychange', queueRecovery);
  }
  const sameSnapshot = (a, b) => (!a && !b) || sameEnvelope(a, b);
  function newAuthGuard() {
    const revision = sessionRevision, generation = signoutGeneration(), baseline = readStoredEnvelope({ includeMemory: false });
    return () => {
      assertSessionRevision(revision);
      if (generation !== signoutGeneration() || !sameSnapshot(baseline, readStoredEnvelope({ includeMemory: false }))) throw staleSession();
    };
  }
  async function verifyNewSession(record, assertCurrent = newAuthGuard(), requireReview = false) {
    assertCurrent();
    const candidate = normalizeSession(record);
    if (!candidate) return validation(SESSION_INDETERMINATE);
    cancelAuthWork(); dropAuthority(); session.set(null); locallySignedOut = false;
    candidate.sid = randomMarker(); candidate.generation = signoutGeneration();
    const attempt = {}; newAuthPending = attempt;
    try { return await validateStoredSession(candidate, { newSession: true, requireReview }); }
    finally { if (newAuthPending === attempt) newAuthPending = null; }
  }

  const AUTH_CALLBACK_KEYS = [
    'access_token', 'refresh_token', 'provider_token', 'provider_refresh_token',
    'token', 'token_hash', 'code', 'token_type', 'expires_in', 'expires_at', 'type',
    'error', 'error_code', 'error_description',
  ];

  const jwtLike = (token) => typeof token === 'string'
    && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);

  // A magic-link callback lands on Puppy Kind with a token hash. The hash is never
  // written to storage and stays only in memory until the visitor explicitly signs in.
  let pendingEmailTokenHash = null;

  /**
   * Consumes a Supabase implicit magic-link callback without exposing credentials.
   * Callback credentials are removed from browser history before the method returns.
   */
  async function consumeAuthCallback() {
    if (typeof window === 'undefined' || !window.location) {
      return { ok: false, authenticated: false, callback: false };
    }

    const url = new URL(window.location.href);
    const hashParams = /[=&]/.test(url.hash)
      ? new URLSearchParams(url.hash.replace(/^#/, '')) : new URLSearchParams();
    // Storefront query keys such as search type=product and discount code are
    // not auth callbacks. Only actual auth credential fields select this path.
    const callback = ['access_token', 'refresh_token', 'provider_token', 'provider_refresh_token', 'token_hash']
      .some((key) => hashParams.has(key) || url.searchParams.has(key))
      || ['error_description'].some((key) => hashParams.has(key) || url.searchParams.has(key));

    if (!callback) {
      // Other first-party scripts can ask for account state while the portal is
      // waiting for the visitor's explicit Sign in click. Keep that callback
      // pending instead of validating a previously cached account or consuming it.
      if (pendingEmailTokenHash) {
        return { ok: true, authenticated: false, callback: true, pendingEmailLink: true };
      }
      return resumeStoredSession();
    }
    return consumeCallbackParams(url, hashParams);
  }

  /* The session already saved on this device, checked with /user. Used on an
     ordinary page load and after an email link that did not work, so a bad
     link never signs out a session that is still valid (2026-10-09). */
  async function resumeStoredSession() {
    const restored = storedSession();
    if (!restored) return { ok: true, authenticated: false, callback: false };
    const checked = await validateStoredSession(restored, { preserveOnIndeterminate: true });
    assertSessionRevision(checked.revision);
    if (checked.status === SESSION_VALID) {
      return { ok: true, authenticated: true, callback: false };
    }
    if (checked.status === SESSION_INDETERMINATE) {
      /* Retain the envelope for automatic retry. Consumers keep neutral
         reconnecting presentation; no unverified token grants benefits. */
      return {
        ok: false, authenticated: verifiedSession(), indeterminate: !verifiedSession(), callback: false,
        authenticationPending: !verifiedSession(), reconnecting: true, retryInMs: authStatus().retryInMs,
        message: 'Reconnecting to your account. We will try again automatically.',
      };
    }
    if (!clearStoredSession({ expected: checked.record || restored, reason: 'session-expired' })) throw staleSession();
    return {
      ok: false, authenticated: false, callback: false,
      message: 'Your sign-in has expired. Please request a new sign-in link.',
    };
  }

  async function consumeCallbackParams(url, hashParams) {
    const read = (key) => hashParams.get(key) || url.searchParams.get(key);
    const accessToken = read('access_token');
    const refreshToken = read('refresh_token');
    const expiresAt = read('expires_at');
    const expiresIn = read('expires_in');
    const tokenHash = read('token_hash');
    const tokenType = String(read('token_type') || '').toLowerCase();
    const callbackType = String(read('type') || '').toLowerCase();
    const authError = read('error') || read('error_code') || read('error_description');

    AUTH_CALLBACK_KEYS.forEach((key) => {
      hashParams.delete(key);
      url.searchParams.delete(key);
    });
    url.hash = hashParams.toString() ? '#' + hashParams.toString() : '';
    const cleanUrl = url.pathname + (url.search ? url.search : '') + (url.hash ? url.hash : '');
    window.history.replaceState(null, '', cleanUrl);

    if (tokenHash && callbackType === 'email' && !authError) {
      pendingEmailTokenHash = tokenHash;
      return { ok: true, authenticated: false, callback: true, pendingEmailLink: true };
    }

    pendingEmailTokenHash = null;
    const expectedType = callbackType === 'magiclink' || callbackType === 'signup';
    if (authError || !jwtLike(accessToken) || tokenType !== 'bearer' || !expectedType) {
      // An expired or already-used link leaves any saved session as it was.
      const kept = await resumeStoredSession();
      return {
        ...kept, ok: false, callback: true,
        message: kept.reconnecting ? kept.message : 'That sign-in link did not work. Please request a new one and try again.',
      };
    }

    const callbackSession = recordFromAuthBody({
      access_token: accessToken,
      refresh_token: refreshToken,
      expires_at: expiresAt,
      expires_in: expiresIn,
    });
    const checked = callbackSession ? await verifyNewSession(callbackSession) : validation(SESSION_INVALID);
    assertSessionRevision(checked.revision);
    if (checked.status !== SESSION_VALID) {
      if (checked.status === SESSION_INVALID) clearStoredSession();
      return {
        ok: false, authenticated: false, callback: true,
        message: checked.status === SESSION_INDETERMINATE
          ? 'We could not verify that sign-in link right now. Please try again.'
          : 'That sign-in link did not work. Please request a new one and try again.',
      };
    }
    // validateStoredSession() has just seen /user accept this token.
    storeSession(session.record(), { verified: true });
    return { ok: true, authenticated: true, callback: true };
  }

  /**
   * Exchanges a pending one-use token hash only after an explicit visitor click.
   * No token hash is persisted; only a verified refreshable session is retained.
   */
  async function completePendingEmailSignIn() {
    const assertCurrent = newAuthGuard();
    const tokenHash = pendingEmailTokenHash;
    if (typeof tokenHash !== 'string' || !/^[A-Za-z0-9_-]{20,512}$/.test(tokenHash)) {
      pendingEmailTokenHash = null;
      const err = new Error('invalid_email_link');
      err.userMessage = 'That sign-in link is no longer active. Please request a new one.';
      throw err;
    }

    let res;
    try {
      res = await authRequest('/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: CFG.anonKey },
        body: JSON.stringify({ token_hash: tokenHash, type: 'email' }),
        credentials: 'omit',
      });
    } catch {
      const err = new Error('email_link_verification_unavailable');
      err.sessionIndeterminate = true;
      err.userMessage = 'We could not verify that sign-in link right now. Please try again.';
      throw err;
    }
    const body = res.body;
    assertCurrent();
    const source = body && body.session && typeof body.session === 'object' ? body.session : body;
    const tokenType = String(source && source.token_type || '').toLowerCase();
    const verifiedSession = res.ok && tokenType === 'bearer' ? recordFromAuthBody(body) : null;
    const checked = !res.ok
      ? validation(res.status === 400 || res.status === 401 || res.status === 403
        ? SESSION_INVALID
        : SESSION_INDETERMINATE)
      : (verifiedSession
        ? await verifyNewSession(verifiedSession, assertCurrent)
        : validation(SESSION_INDETERMINATE));
    assertSessionRevision(checked.revision);
    if (checked.status !== SESSION_VALID) {
      if (checked.status === SESSION_INVALID) {
        pendingEmailTokenHash = null;
        // /verify refused the link itself (expired or already used): a session
        // saved on this device stays and is re-checked. A link that verified but
        // whose new token /user then rejected still clears, as before.
        if (res.ok) clearStoredSession();
        else if (storedSession()) void recoverSession();
      }
      const err = new Error(checked.status === SESSION_INDETERMINATE
        ? 'email_link_verification_unavailable'
        : 'email_link_not_accepted');
      err.sessionIndeterminate = checked.status === SESSION_INDETERMINATE;
      err.userMessage = checked.status === SESSION_INDETERMINATE
        ? 'We could not verify that sign-in link right now. Please try again.'
        : 'That sign-in link is no longer active. Please request a new one.';
      throw err;
    }
    // validateStoredSession() has just seen /user accept this token.
    pendingEmailTokenHash = null;
    storeSession(session.record(), { verified: true });
    return { ok: true };
  }

  /* Merchant review sign-in. One exact username maps to one internal Auth email;
     nothing else is accepted, so no customer can use a password here. The password
     is sent once to Supabase Auth and is never stored, logged or returned. The
     session is kept only after /user positively reports the server-owned
     app_metadata.merchant_review flag. */
  const REVIEW_USERNAME = 'merchant-review';
  const REVIEW_AUTH_EMAIL = 'merchant-review@puppykind.org';
  const REVIEW_REJECTED = 'That username or password is not right.';
  const REVIEW_UNAVAILABLE = 'We could not sign you in right now. Please try again.';

  function reviewSignInError(code, message, indeterminate = false) {
    const err = new Error(code);
    err.userMessage = message;
    err.sessionIndeterminate = indeterminate;
    return err;
  }

  // Refusal exemption only: a retained demo-shaped envelope may try its password
  // again. This hint never sets sessionVerified, reviewToken, auth or benefits.
  function reviewSessionHint(record) {
    try {
      const part = String(record && record.accessToken || '').split('.')[1];
      const claims = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return claims && claims.app_metadata && claims.app_metadata.merchant_review === true;
    } catch (_) { return false; }
  }

  async function signInReview({ username, password } = {}) {
    // Dedicated demo entry must never replace or clear another customer's session.
    if ((verifiedSession() && session.get() && !isReviewAccount()) || (storedSession() && !isReviewAccount() && !reviewSessionHint(storedSession()))) {
      throw reviewSignInError('review_existing_customer_session', 'You’re signed in to a customer account in this browser. Sign out first, or open the demo link in a private window.');
    }
    if (resolveTransport() !== 'supabase') {
      throw reviewSignInError('review_signin_unavailable', REVIEW_UNAVAILABLE, true);
    }
    if (String(username || '').trim().toLowerCase() !== REVIEW_USERNAME
        || typeof password !== 'string' || !password) {
      throw reviewSignInError('review_credentials_rejected', REVIEW_REJECTED);
    }
    const assertCurrent = newAuthGuard();
    let res;
    try {
      res = await authRequest('/token?grant_type=password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: CFG.anonKey },
        body: JSON.stringify({ email: REVIEW_AUTH_EMAIL, password }),
        credentials: 'omit',
      });
    } catch {
      throw reviewSignInError('review_signin_unavailable', REVIEW_UNAVAILABLE, true);
    }
    const body = res.body;
    assertCurrent();
    if (!res.ok) {
      throw res.status === 400 || res.status === 401 || res.status === 403
        ? reviewSignInError('review_credentials_rejected', REVIEW_REJECTED)
        : reviewSignInError('review_signin_unavailable', REVIEW_UNAVAILABLE, true);
    }
    const source = body && body.session && typeof body.session === 'object' ? body.session : body;
    const record = String(source && source.token_type || '').toLowerCase() === 'bearer'
      ? recordFromAuthBody(body) : null;
    const checked = record ? await verifyNewSession(record, assertCurrent, true) : validation(SESSION_INDETERMINATE);
    assertSessionRevision(checked.revision);
    if (checked.status !== SESSION_VALID || !isReviewAccount()) {
      const token = record && record.accessToken;
      clearStoredSession({ reason: 'invalid-session' });
      if (jwtLike(token)) {
        fetch(authBaseUrl() + '/logout?scope=local', {
          method: 'POST',
          headers: { apikey: CFG.anonKey, Authorization: 'Bearer ' + token },
          credentials: 'omit',
        }).catch(() => {});
      }
      throw checked.status === SESSION_INDETERMINATE
        ? reviewSignInError('review_signin_unavailable', REVIEW_UNAVAILABLE, true)
        : reviewSignInError('review_credentials_rejected', REVIEW_REJECTED);
    }
    // validateStoredSession() has just seen /user accept this token with the flag.
    storeSession(session.record(), { verified: true, reason: 'review-signin' });
    return { ok: true, reviewAccount: true };
  }

  /* Refusal for anything that would place a purchase from the review identity. */
  const REVIEW_NO_PURCHASE = 'Test account. No purchase will be placed.';
  function refuseReviewPurchase() {
    if (!isReviewAccount()) return;
    const e = new Error('review_account_no_purchase');
    e.userMessage = REVIEW_NO_PURCHASE;
    throw e;
  }

  // ---------------------------------------------------------------- contact transport
  /**
   * Builds the exact body the store's own contact route accepts. Separated out so the
   * request shape can be asserted in a test without sending anything.
   */
  function buildContactRequest({ email, consentText }) {
    const body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[email]', email);
    body.set('contact[body]', consentText);
    return { url: '/contact', method: 'POST', body };
  }

  /* `accepted` is the exact response the trial was authorized against. The
     recorded consent has to quote those terms, because those are the terms the
     shopper was shown; a cache update that lands mid-action must not rewrite
     what somebody already agreed to. */
  function consentText({ email, offer: accepted } = {}) {
    const o = accepted && typeof accepted === 'object' ? accepted : offer();
    return [
      'Pup Club trial request.',
      `Email: ${email}`,
      `Accepted offer version: ${o.consentVersion || 'unversioned'}`,
      `Terms accepted: ${offerSentence(o)}`,
      `Recurring schedule accepted: ${renewalPhrase(o)} until canceled.`,
      'No payment method was provided and no charge was authorized.',
    ].join('\n');
  }

  // ---------------------------------------------------------------- public API
  /**
   * Start the 21-day trial.
   * Resolves { ok, transport, recorded, status?, trialEndAt?, nextRenewalAt?, message }.
   * Rejects with a real error the caller must show. Never resolves with a fabricated
   * membership or a charge.
   */
  async function startTrial({ email, consentAccepted } = {}) {
    refuseReviewPurchase();
    /* Forced network refresh, then the whole contract, immediately before the
       enrollment unlock. A cached offer — including one this page fetched on
       load — may not authorize a trial start. */
    const readiness = await freshOfferReadiness();
    if (!readiness.ready) {
      const e = new Error('enrollment_not_open');
      e.reasons = readiness.reasons;
      e.userMessage = readiness.fresh
        ? ENROLLMENT_CLOSED_MESSAGE
        : 'We could not verify the current Pup Club terms, so nothing has been started or charged. Please try again.';
      throw e;
    }
    /* The exact response this trial was authorized against. Every term the rest
       of this action quotes, records or shows is read off this object: the offer
       hash sent with the consent, the consent version and sentence in the
       contact record, and the trial length in the confirmation. A concurrent
       refresh may replace the shared cache at any point below; it may not move
       the terms of an action already in progress. */
    const o = readiness.offer;
    if (consentAccepted !== true) {
      const e = new Error('consent_required');
      e.userMessage = 'Please confirm you accept the Pup Club terms.';
      throw e;
    }
    const identityEmail = emailLooksValid(email) ? email : authenticatedEmail;
    if (!emailLooksValid(identityEmail)) {
      const e = new Error('invalid_email');
      e.userMessage = 'Sign in to your Puppy Kind account before starting Pup Club.';
      throw e;
    }
    const addr = String(identityEmail).trim().toLowerCase();
    const transport = resolveTransport();

    if (transport === 'supabase') {
      const consent = await rpc('pup_club_record_consent', {
        p_email: addr, p_offer_hash: o.offerHash, p_surface: 'join-pup-club',
      });
      if (!consent || !consent.consentId) {
        const e = new Error('consent_not_recorded');
        e.userMessage = 'We could not record your acceptance. Please try again.';
        throw e;
      }
      // Enrollment needs an authenticated identity. With a session token present we
      // complete it now; without one the recorded consent is carried to sign-in.
      if (!session.get()) {
        return {
          ok: true, transport, recorded: 'consent', consentId: consent.consentId,
          message: `Your acceptance is recorded for ${addr}. Sign in to finish starting your trial.`,
          next: 'signin',
        };
      }
      const enrolled = await rpc('pup_club_enroll',
        { p_consent_id: consent.consentId }, { token: session.get() });
      return {
        ok: true, transport, recorded: 'membership',
        status: enrolled.status, trialStartAt: enrolled.trialStartAt,
        trialEndAt: enrolled.trialEndAt, nextRenewalAt: enrolled.nextRenewalAt,
        renewalCents: enrolled.renewalCents, intervalDays: enrolled.intervalDays,
        message: enrolled.created
          ? `Your ${o.trialDays}-day trial has started.`
          : 'You are already a Pup Club member.',
        next: 'portal',
      };
    }

    const req = buildContactRequest({
      email: addr,
      consentText: consentText({ email: addr, offer: o }),
    });
    const res = await fetch(req.url, {
      method: req.method,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: req.body.toString(),
      credentials: 'same-origin',
    });
    if (!res.ok && res.status !== 0) {
      const e = new Error('contact_route_' + res.status);
      e.userMessage = 'We could not send your request. Please try again.';
      throw e;
    }
    return {
      ok: true, transport, recorded: 'request',
      // Says exactly what happened. No membership, no charge, no payment method.
      message: `Your trial request is recorded for ${addr}, with the terms you accepted: `
             + offerSentence(o),
      next: 'terms',
    };
  }

  function membershipState() {
    if (resolveTransport() !== 'supabase' || !verifiedSession() || !session.get()) return Promise.resolve(null);
    const token = session.get(), revision = sessionRevision;
    if (membershipFlight && membershipFlight.token === token && membershipFlight.revision === revision) return membershipFlight.promise;
    const entry = { token, revision, promise: null };
    entry.promise = Promise.resolve().then(async () => {
      assertVerifiedSession(token, revision);
      const result = await rpc('pup_club_membership_state', {}, { token });
      assertVerifiedSession(token, revision);
      return result;
    }).finally(() => { if (membershipFlight === entry) membershipFlight = null; });
    membershipFlight = entry;
    return entry.promise;
  }

  async function orderHistory(before = null) {
    const token = requireLifecycleSession();
    if (before !== null && (typeof before !== 'string' || !/^[1-9][0-9]{0,29}$/.test(before))) {
      throw new Error('invalid_cursor');
    }
    const revision = sessionRevision;
    const result = await rpc('pup_club_order_history', { p_before: before }, { token });
    assertVerifiedSession(token, revision);
    return result;
  }

  function membershipEntitlement(member) {
    const status = String(member && member.status || '').toLowerCase();
    return {
      status: status || (member && member.member === false ? 'none' : 'unknown'),
      // The server is the only entitlement authority. A lifecycle label alone
      // must never restore VIP pricing after expiry or while a billing event is
      // still unresolved.
      benefitsActive: !!(member && member.member === true && member.benefitsActive === true),
    };
  }

  /* One first-party account contract for the whole theme. Authentication and
     membership entitlement deliberately remain different facts: a person may be
     signed in without any active Pup Club benefit, and a membership RPC failure
     must never sign that person out or manufacture access. */
  async function readAccountState() {
    const auth = newAuthPending
      ? { authenticated: false, indeterminate: true, authenticationPending: true, reconnecting: true, retryInMs: 1500 }
      : await consumeAuthCallback();
    if (auth.pendingEmailLink) {
      // A mail scanner can open a magic-link URL. Rendering the explicit
      // confirmation surface is the only path that may call /auth/v1/verify.
      return {
        authenticated: false,
        reviewAccount: false,
        membership: null,
        entitlement: membershipEntitlement(null),
        auth,
      };
    }
    /* Reconnecting is a neutral pending state. A retained envelope grants no
       identity or benefit until /user positively accepts its exact token. */
    if (auth.indeterminate) {
      return {
        authenticated: false,
        reviewAccount: false,
        authenticationIndeterminate: true,
        authenticationPending: true, reconnecting: true, retryInMs: auth.retryInMs,
        membership: null,
        entitlement: membershipEntitlement(null),
        auth: { ...auth, authenticated: false },
      };
    }
    if (!auth.authenticated) {
      return { authenticated: false, reviewAccount: false, membership: null, entitlement: membershipEntitlement(null), auth };
    }
    const token = session.get();
    const revision = sessionRevision;
    assertVerifiedSession(token, revision);
    try {
      const membership = await membershipState();
      assertVerifiedSession(token, revision);
      return {
        authenticated: true,
        reviewAccount: isReviewAccount(),
        sessionRevision: revision,
        membership,
        entitlement: membershipEntitlement(membership),
        auth,
      };
    } catch (error) {
      assertVerifiedSession(token, revision);
      if (error && error.stale) throw error;
      return {
        authenticated: true,
        reviewAccount: isReviewAccount(),
        sessionRevision: revision,
        membership: null,
        entitlement: membershipEntitlement(null),
        membershipError: true,
        auth: { ...auth, message: 'You’re signed in, but we couldn’t load your Pup Club status yet. Your benefits stay protected until we can verify them.' },
      };
    }
  }

  function accountState() {
    if (accountFlight) return accountFlight;
    const promise = Promise.resolve().then(() => readAccountState());
    accountFlight = promise;
    promise.finally(() => { if (accountFlight === promise) accountFlight = null; }).catch(() => {});
    return promise;
  }

  /* Local state is cleared first, so clicking Sign out works even if the remote
     logout endpoint is temporarily unavailable. The remote call revokes the Supabase
     refresh session when available; its result is never displayed as a billing claim. */
  async function signOut() {
    const accessToken = session.get();
    clearStoredSession({ reason: 'signed-out' });
    if (resolveTransport() !== 'supabase' || !jwtLike(accessToken)) return { ok: true, remote: false };
    try {
      const res = await fetch(authBaseUrl() + '/logout?scope=local', {
        method: 'POST',
        headers: { apikey: CFG.anonKey, Authorization: 'Bearer ' + accessToken },
        credentials: 'omit',
      });
      return { ok: true, remote: res.ok };
    } catch {
      return { ok: true, remote: false };
    }
  }

  /* Hosted V2 lifecycle contract.
     ---------------------------------------------------------------------------
     pup_club_request_pause_v2(p_idempotency_key uuid, p_days integer)
     pup_club_request_resume_v2(p_idempotency_key uuid)
     pup_club_request_cancel_v3(p_idempotency_key uuid, p_channel text)
     pup_club_prepare_rejoin(p_idempotency_key uuid, p_offer_id uuid)

     Each of these records a *request*. The caller passes the UUID it minted for the
     user's action, and passes the same UUID again for any retry of that action, so
     the server can collapse duplicates instead of transitioning twice. The membership
     is selected by the authenticated identity in the bearer token; nothing about which
     membership to change comes from the browser.

     Nothing here paints a result. The provider capability is currently unavailable, so
     a caller must read the refreshed membership state before showing any pause, resume,
     cancel or rejoin as done. A rejected or failed call leaves the visible state alone. */
  function requireLifecycleSession() {
    // A retained-but-unvalidated envelope is not a signed-in member. Lifecycle
    // mutations need the positively verified session, same as every other surface.
    if (resolveTransport() !== 'supabase' || !verifiedSession() || !session.get()) {
      const e = new Error('not_signed_in');
      e.userMessage = 'Sign in to your Puppy Kind account to change your membership.';
      throw e;
    }
    return session.get();
  }

  function lifecycleKey(key) {
    if (uuidLike(key)) return key;
    if (key === undefined || key === null || key === '') return newIdempotencyKey();
    const e = new Error('invalid_idempotency_key');
    e.userMessage = 'We could not start that change safely. Please reload the page and try again.';
    throw e;
  }

  /* The provider capability each lifecycle action actually needs, named per
     action rather than collapsed into one flag. Pausing suspends a recurring
     schedule, so it needs the recurring capture; resuming starts charging again,
     so it also needs the stored payment method; cancelling needs a configured
     provider to record the stop against. Rejoin is not here: it begins a new
     commerce intent and is held to the whole enrollment contract instead. */
  const LIFECYCLE_CAPABILITY = {
    pause: ['configured', 'recurringCaptureConfigured'],
    resume: ['configured', 'recurringCaptureConfigured', 'paymentMethodCaptureConfigured'],
    cancel: ['configured'],
  };

  /* The refusal the theme already shows while the hosted provider is disabled.
     It says what is true and what did not happen, and promises no date. */
  const PROVIDER_UNAVAILABLE_MESSAGE =
    'Membership changes are not connected to the payment subscription yet. Nothing has been changed.';

  function providerRefusal(reasons) {
    const e = new Error('membership_provider_unconfigured');
    e.reasons = reasons;
    e.userMessage = PROVIDER_UNAVAILABLE_MESSAGE;
    return e;
  }

  /* Forced network read of the provider contract, immediately before the V2 RPC
     that would act on it. The cached offer is deliberately not consulted: while
     the hosted provider is disabled this is what makes every lifecycle control
     fail closed, leaving the membership and the visible state exactly as they
     were. A failed refresh is a refusal, not a fallback.
     ---------------------------------------------------------------------------
     "Not consulted" means the exact response to this call's own request is the
     only record inspected, and the same object is handed back. Reading the cache
     after the await would reintroduce the fault this replaced: only the newest
     request may replace the cache, so an earlier read whose server reported the
     provider disabled would be judged against a newer, capable record, and an
     action would proceed on a contract that never authorized it. */
  async function requireProviderCapability(action) {
    /* The verified review identity has no provider effects: the server routes its
       lifecycle RPCs to private test state. Real customers stay fail-closed below. */
    if (isReviewAccount()) return exactOffer(authoritativeOffer);
    let response = null;
    try {
      response = await refreshOffer({ force: true });
    } catch (_) {
      throw providerRefusal(['provider_contract_unavailable']);
    }
    if (!response || typeof response !== 'object') {
      throw providerRefusal(['provider_contract_unavailable']);
    }
    const current = exactOffer(response);
    const billing = current.billing || {};
    const missing = (LIFECYCLE_CAPABILITY[action] || []).filter((clause) => billing[clause] !== true);
    if (typeof billing.providerKey !== 'string' || !billing.providerKey.trim()) {
      missing.push('provider_key_unavailable');
    }
    if (missing.length) throw providerRefusal(missing);
    return current;
  }

  /* Exposed so a surface that wraps a lifecycle call can ask this contract
     instead of re-deciding from a cached offer of its own. `offer` is the exact
     response the verdict was read from, so a caller needing a term from it takes
     that one rather than looking the cache up again. */
  function providerCapability(action) {
    return requireProviderCapability(action).then(
      (current) => ({ ready: true, reasons: [], offer: current }),
      (error) => ({
        ready: false,
        reasons: (error && error.reasons) || ['membership_provider_unconfigured'],
        offer: null,
      }),
    );
  }

  /* Pause and resume act on the caller's own membership, and the server gates
     them on that membership's provider: pup_club_queue_lifecycle_request checks
     pup_club_require_provider_capabilities(m.provider_key, ...). The published
     offer's billing block is the default enrollment provider only (pup_club_offer
     joins on is_default), so it cannot answer for a membership held on another
     provider. This reads the server's own verdict for that membership,
     allowedActions, from a forced membership_state request of its own,
     immediately before the V2 RPC. No in-flight or cached state is reused, and
     anything but an exact true is the same refusal as before. */
  async function requireMembershipCapability(action, token) {
    const revision = sessionRevision;
    let state = null;
    try {
      assertVerifiedSession(token, revision);
      state = await rpc('pup_club_membership_state', {}, { token });
    } catch (error) {
      if (error && error.stale) throw error;
      throw providerRefusal(['membership_state_unavailable']);
    }
    assertVerifiedSession(token, revision);
    const allowed = state && typeof state === 'object' && !Array.isArray(state)
      && state.allowedActions && typeof state.allowedActions === 'object'
      ? state.allowedActions : null;
    if (!allowed) throw providerRefusal(['membership_state_unavailable']);
    if (allowed[action] !== true) throw providerRefusal(['membership_action_not_allowed']);
    return state;
  }

  const REVIEW_NO_MEMBERSHIP_CHANGE = 'This is a test account. No membership changes can be made.';
  function refuseReviewMembershipChange() {
    if (!isReviewAccount()) return;
    const error = new Error('review_account_no_membership_change');
    error.userMessage = REVIEW_NO_MEMBERSHIP_CHANGE;
    throw error;
  }

  async function requestPause(days, idempotencyKey) {
    refuseReviewMembershipChange();
    const token = requireLifecycleSession();
    const p_days = Number(days);
    if (!Number.isInteger(p_days) || p_days < 1) {
      const e = new Error('invalid_pause_days');
      e.userMessage = 'We could not read that pause length. Nothing has been changed.';
      throw e;
    }
    await requireMembershipCapability('pause', token);
    return rpc('pup_club_request_pause_v2',
      { p_idempotency_key: lifecycleKey(idempotencyKey), p_days }, { token });
  }

  async function requestResume(idempotencyKey) {
    refuseReviewMembershipChange();
    const token = requireLifecycleSession();
    await requireMembershipCapability('resume', token);
    return rpc('pup_club_request_resume_v2',
      { p_idempotency_key: lifecycleKey(idempotencyKey) }, { token });
  }

  async function requestCancel(reason, idempotencyKey) {
    refuseReviewMembershipChange();
    const token = requireLifecycleSession();
    const revision = sessionRevision;
    const receipt = await rpc('pup_club_request_cancel_v3', {
      p_idempotency_key: lifecycleKey(idempotencyKey),
      p_channel: 'portal',
    }, { token });
    assertVerifiedSession(token, revision);
    // This is a durable intake receipt, never proof that recurring billing stopped.
    return receipt;
  }

  /* Rejoin starts a new commerce intent, so it is held to the whole enrollment
     contract on a forced network refresh — not just to the provider capability —
     and the offer UUID it quotes is read from that same forced response. A value
     the browser is holding, including one this page read a moment ago, is not
     trusted: a stale page must not rejoin anyone on retired terms. The parameter
     is kept for callers that still pass their last-known id; it is ignored. */
  async function prepareRejoin(offerId, idempotencyKey) {
    const token = requireLifecycleSession();
    refuseReviewPurchase();
    const readiness = await freshOfferReadiness();
    if (!readiness.ready) {
      const e = new Error('enrollment_not_open');
      e.reasons = readiness.reasons;
      e.userMessage = readiness.fresh
        ? ENROLLMENT_CLOSED_MESSAGE
        : 'We could not load the current Pup Club terms, so we have not started your rejoin. Please try again in a moment.';
      throw e;
    }
    /* Quoted from the exact response whose full readiness just passed — never
       from the shared cache, which a newer request may already own and an older
       one may be about to overwrite. The id and the verdict must come from the
       same read, or rejoin can be authorized by one offer and placed against
       another. */
    const p_offer_id = readiness.offer ? readiness.offer.offerId : null;
    if (!uuidLike(p_offer_id)) {
      const e = new Error('offer_unavailable');
      e.userMessage = 'We could not load the current Pup Club terms, so we have not started your rejoin. Please try again in a moment.';
      throw e;
    }
    return rpc('pup_club_prepare_rejoin',
      { p_idempotency_key: lifecycleKey(idempotencyKey), p_offer_id }, { token });
  }

  const api = {
    offer,
    offerSentence,
    renewalPhrase,
    money,
    transport: resolveTransport,
    refreshOffer,
    // The single enrollment gate. Every join, upsell and add-to-cart path asks
    // this, so the four provider clauses and the terms version cannot drift.
    offerReadiness,
    // The pre-mutation form of the same gate: forced network refresh first, then
    // that one contract, evaluated against the exact response to that request.
    // Cart adds, checkout/enrollment unlocks and the surfaces that reveal an
    // acquisition claim ask this and nothing weaker. A ready verdict carries
    // that response as `offer`; a caller acting on the verdict must take every
    // term, id and hash from it rather than calling offer() again, which reads a
    // cache only the newest request in flight is allowed to own.
    freshOfferReadiness,
    enrollmentReady,
    // Per-action provider contract, read from the network at the moment it is
    // asked. Lifecycle wrappers ask this instead of keeping a cached copy.
    providerCapability,
    /* Pause/resume and rejoin retain their fresh provider/offer gates.
       Cancellation records authenticated intent even while dispatch is disabled;
       wrappers must not add a cached provider gate to that request. */
    lifecycleCapabilityEnforced: true,
    requiredTermsVersion: () => REQUIRED_TERMS_VERSION,
    requiredOfferContract: () => ({
      termsVersion: REQUIRED_TERMS_VERSION,
      currency: REQUIRED_CURRENCY,
      trialDays: REQUIRED_TRIAL_DAYS,
      renewalCents: REQUIRED_RENEWAL_CENTS,
      intervalDays: REQUIRED_INTERVAL_DAYS,
    }),
    enrollmentClosedMessage: () => ENROLLMENT_CLOSED_MESSAGE,
    providerUnavailableMessage: () => PROVIDER_UNAVAILABLE_MESSAGE,
    // Verified sessions only. A retained-but-unvalidated envelope reports false.
    signedIn: verifiedSession,
    verifiedEmail: () => verifiedSession() ? authenticatedEmail : null,
    sessionHint,
    authStatus,
    retrySession: () => recoverSession(),
    // Verified merchant review session only; never a query or browser flag.
    isReviewAccount,
    hasReviewSessionHint: () => reviewSessionHint(storedSession()),
    reviewNoPurchaseMessage: () => REVIEW_NO_PURCHASE,
    signInReview,
    // Correlation only: neither method grants or changes authentication.
    sessionRevision: () => sessionRevision,
    sessionIsCurrent,
    setSession: storeSessionToken,
    clearSession: () => clearStoredSession({ reason: 'session-cleared' }),
    signOut,
    consumeAuthCallback,
    hasPendingEmailLink: () => !!pendingEmailTokenHash,
    completePendingEmailSignIn,
    startTrial,
    membershipState,
    orderHistory,
    membershipEntitlement,
    accountState,
    // Callers mint one key per user action with newIdempotencyKey() and hand the
    // same key back on a retry of that action.
    newIdempotencyKey,
    pause: requestPause,
    resume: requestResume,
    cancel: requestCancel,
    prepareRejoin,
    // Exposed for tests: asserts the contact request shape without sending it.
    buildContactRequest,
    consentText,
  };

  installCrossTabSessionSync();
  if (typeof window !== 'undefined') window.PKPupClub = api;
  if (resolveTransport() === 'supabase') void refreshOffer().catch(() => {});
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
